import i18n from 'i18n-js';

const createResponse = (showPrompt, type, message = '') => ({
  showPrompt,
  type,
  message
});

export const checkIsActionBarCode = barcodeInput => {
  if (barcodeInput === 'PASS' || barcodeInput === 'CLICKSCAN') {
    return true;
  }
  return false;
};

export const checkIsRestartCode = (barcodeInput, scanPackSettings) => {
  if (!scanPackSettings?.restart_code_enabled) {
    return false;
  }
  return barcodeInput?.toString() === scanPackSettings?.restart_code;
};

export const checkClickScanEnabled = (item, scanPackSettings, overridePassButton, isShiftKey = false) => {
  if (overridePassButton || isShiftKey) {
    return createResponse(false, 'ClickScanEnabled');
  }
  
  if (scanPackSettings?.enable_click_sku) {
    const clickScanEnabled = item?.click_scan_enabled;

    if (clickScanEnabled === 'on_with_confirmation') {
      return createResponse(true, 'ClickScanConfirmation');
    }

    if (clickScanEnabled === 'on') {
      return createResponse(false, 'ClickScanEnabled');
    }
  }

  return createResponse(
    true,
    'ClickScanDisabled',
    i18n.t('scanpackItem.Scanpack_pass_fun_msg')
  );
};

export const checkProductInstructions = (item, generalSettings) => {
  const confSetting = generalSettings?.conf_code_product_instruction;

  if (confSetting === 'never' || item?.scanned_qty > 0) {
    return createResponse(false, 'ProductInstructions');
  }

  if (confSetting === 'always') {
    if (item?.instruction?.length > 0) {
      return createResponse(true, 'ProductInstructionsConfirmation');
    }
    return createResponse(false, 'ProductInstructions');
  }

  if (confSetting === 'optional' && !item?.confirmation) {
    return createResponse(false, 'ProductInstructions');
  }

  if (item?.confirmation && item?.instruction?.length > 0) {
    return createResponse(true, 'ProductInstructionsConfirmation');
  }

  return createResponse(false, 'ProductInstructions');
};

export const checkSerialRecordingEnabled = (item, isSecondSerial = false) => {
  if (item?.record_serial && !isSecondSerial) {
    return createResponse(true, 'SerialRecordingOne', i18n.t('serialRecord.serial_number'));
  }
  if (item?.second_record_serial) {
    return createResponse(true, 'SerialRecordingTwo', i18n.t('serialRecord.serial_number_two'));
  }
  return createResponse(false, 'SerialRecording');
};

export const isActionCode = (code, scanPackSettings) => {
  const stringCode = code?.toString();
  return (
    stringCode === scanPackSettings?.restart_code ||
    stringCode === scanPackSettings?.not_fulfilled_barcode ||
    stringCode === scanPackSettings?.note_from_packer_code ||
    stringCode === scanPackSettings?.service_issue_code ||
    stringCode === scanPackSettings?.click_scan_barcode ||
    stringCode === scanPackSettings?.pass_scan_barcode ||
    stringCode === scanPackSettings?.add_next_barcode ||
    stringCode === scanPackSettings?.remove_barcode ||
    stringCode === scanPackSettings?.partial_barcode ||
    stringCode === scanPackSettings?.scanned_barcode ||
    stringCode === scanPackSettings?.skip_code ||
    stringCode === 'BOXPREV' ||
    stringCode === 'BOXNEXT' ||
    stringCode === 'NEXTBOX'
  );
};

const checkConfirmationCodes = (input, userInfo) => {
  const confirmationAll = userInfo?.all_users_confirmation_code?.some(element =>
    element?.includes(input)
  );
  const allBarCodes = userInfo?.all_barcodes?.some(element =>
    element?.includes(input)
  );
  
  return { confirmationAll, allBarCodes };
};

const validatePrefixes = (input, prefixes, userInfo, scanPackSettings) => {
  const { confirmationAll, allBarCodes } = checkConfirmationCodes(input, userInfo);
  const allActionCode = isActionCode(input, scanPackSettings);
  
  let access = false;
  
  prefixes.forEach(serial => {
    const prefixLength = serial.length;
    const userInput = input.slice(0, prefixLength);
    
    if (
      userInput === serial &&
      !confirmationAll &&
      !allBarCodes &&
      !allActionCode
    ) {
      access = true;
    }
  });
  
  return {
    access,
    hasConflict: confirmationAll || allBarCodes || allActionCode
  };
};

const getValidationErrorMessage = (hasConflict) => {
  if (hasConflict) {
    return i18n.t('scanpackItem.Scanpack_barcode_found_or_special_code_msg');
  }
  return i18n.t('scanpackItem.Scanpack_required_serial_msg');
};

export const nextAdjacentScanCount = (lastScannedSku, adjacentScans, scannedSku) =>
  scannedSku === lastScannedSku ? adjacentScans + 1 : 1;

export const quantityAfterScanDeduction = (typedQuantity, adjacentScans) =>
  adjacentScans === 1 ? Math.max(0, typedQuantity - 1) : typedQuantity;

const typeScanPrompt = needsConfirmation => ({
  shouldShowPrompt: true,
  promptType: needsConfirmation ? 'type_scan_confirmation' : 'type_scan',
  message: '',
  playError: false
});

export const checkTypeScanCode = (barcodeValue, scanPackSettings, currentItem, typeScanState, currentOrder) => {
  if (!scanPackSettings?.type_scan_code_enabled || scanPackSettings?.type_scan_code !== barcodeValue) return null;

  if (!currentItem) return null;

  if (typeScanState !== currentItem.sku) {
    return {
      shouldShowPrompt: true,
      promptType: 'alert',
      message: i18n.t('scanpackItem.Scanpack_type_count_msg'),
      playError: true
    };
  }

  const unScannedItems = currentOrder?.unscanned_items || [];
  const firstItem = unScannedItems[0];
  let needsConfirmation = false;

  if (firstItem?.product_type === 'individual' && firstItem?.child_items?.length > 0) {
    const childItem = firstItem.child_items[0];

    if (typeScanState !== childItem.sku) {
      return {
        shouldShowPrompt: true,
        promptType: 'alert',
        message: i18n.t('scanpackItem.Scanpack_type_count_msg'),
        playError: true
      };
    }

    const childTypeScanEnabled = childItem?.type_scan_enabled;
    const parentTypeScanEnabled = firstItem?.type_scan_enabled;

    if (childTypeScanEnabled === 'Disable' || childTypeScanEnabled === 'off' ||
        parentTypeScanEnabled === 'Disable' || parentTypeScanEnabled === 'off') {
      return {
        shouldShowPrompt: true,
        promptType: 'alert',
        message: i18n.t('scanpackItem.Disable_type_count_msg'),
        playError: true
      };
    }

    needsConfirmation = childTypeScanEnabled === 'on_with_confirmation';
  } else {
    const typeScanEnabled = currentItem?.type_scan_enabled;
    const firstItemTypeScanEnabled = firstItem?.type_scan_enabled;

    if (typeScanEnabled === 'Disable' || typeScanEnabled === 'off' ||
        firstItemTypeScanEnabled === 'Disable' || firstItemTypeScanEnabled === 'off') {
      return {
        shouldShowPrompt: true,
        promptType: 'alert',
        message: i18n.t('scanpackItem.Disable_type_count_msg'),
        playError: true
      };
    }

    needsConfirmation = typeScanEnabled === 'on_with_confirmation';
  }

  return typeScanPrompt(needsConfirmation);
};

export const validateSerialRecord = (input, scanPackSettings, userInfo) => {
  if (!scanPackSettings?.require_serial_lot) {
    return { isValid: true, errorMessage: '' };
  }

  if (!scanPackSettings?.valid_prefixes) {
    return { isValid: true, errorMessage: '' };
  }
  
  const prefixes = scanPackSettings.valid_prefixes.split(',');
  const { access, hasConflict } = validatePrefixes(input, prefixes, userInfo, scanPackSettings);
  
  if (access) {
    return { isValid: true, errorMessage: '' };
  }
  
  return {
    isValid: false,
    errorMessage: getValidationErrorMessage(hasConflict)
  };
};

export const validateConfirmationCode = (userInput, userCode, strictCC) => {
  if (userInput === '') {
    return { isMatch: false, isEmpty: true, allowBypass: !strictCC };
  }
  const isMatch = userInput === userCode;
  return { isMatch, isEmpty: false, allowBypass: !strictCC };
};

// =============================================================
// WMS Lot Verification (L1-L3 ladder)
//
// Mirrors the backend's Groovepacker::Inventory::LotScanGate. The client
// pre-validates so the packer never has to round-trip to the server just to
// find out a code/lot was wrong — the same UX shape as click-scan
// confirmation. The server gate is still authoritative defense-in-depth for
// any request that bypasses the client (offline replay, a stale build).
//
// `item.wms_inventory` is present only when the tenant + warehouse are both
// WMS-enabled (Groovepacker::Inventory::LotSnapshot.for_scan). Its absence
// means WMS is off, or off for this product's warehouse — either way the
// ladder never applies and behavior must be byte-identical to WMS-off.
// =============================================================

// L1 is display-only and never interrupts (ScanLadder#asks_for_lot?). Only
// L2/L3 gate the scan, and only when there is a POSITIVE lot position to
// verify against (LotScanGate reason 4) — a zero/negative position is the Z
// ladder's job, not this one.
export const checkWmsLotVerification = (item) => {
  const wmsInventory = item?.wms_inventory;

  if (!wmsInventory || !wmsInventory.lot) {
    return { showPrompt: false, level: null };
  }

  const level = wmsInventory.lot_verification_level;
  if (level !== 'L2' && level !== 'L3') {
    return { showPrompt: false, level: null };
  }

  // Mirrors LotScanGate's reason 4 (`lot_quantity_on_hand.to_i.positive?`). A missing or
  // non-numeric value must be treated as "no positive lot position" and NOT gate the scan:
  // `Number(undefined) <= 0` is false, so without the finite check a malformed snapshot fell
  // through and raised a prompt the server would never have asked for.
  const lotQuantityOnHand = Number(wmsInventory.lot_quantity_on_hand);
  if (!Number.isFinite(lotQuantityOnHand) || lotQuantityOnHand <= 0) {
    return { showPrompt: false, level: null };
  }

  const message = level === 'L2'
    ? i18n.t('wmsLotVerification.confirmationCodeMessage')
    : i18n.t('wmsLotVerification.lotNumberMessage', { lot: wmsInventory.lot });

  return { showPrompt: true, level, message, lot: wmsInventory.lot };
};

// L2 verifies WHO is packing (the packer's own confirmation code, exactly
// like click-scan confirmation). L3 verifies WHAT they are holding (the lot
// itself), compared case- and whitespace-insensitively because it arrives
// off a barcode or a keyboard — mirrors LotScanGate.correct? on the backend.
export const validateWmsLotAnswer = (given, level, item, userInfo) => {
  const trimmed = (given || '').trim();
  if (!trimmed) return { isMatch: false };

  if (level === 'L2') {
    const code = userInfo?.confirmation_code;
    return { isMatch: !!code && trimmed === code };
  }

  if (level === 'L3') {
    const expectedLot = item?.wms_inventory?.lot;
    return {
      isMatch: !!expectedLot && trimmed.toLowerCase() === String(expectedLot).trim().toLowerCase()
    };
  }

  return { isMatch: false };
};

// =============================================================
// WMS Zero-Inventory (Z1-Z3 ladder) — stop-and-receive
//
// CORRECTED 2026-08-07 (QA finding B1): `item.wms_inventory` DOES tell the client which Z rung
// is configured — `zero_inventory_level` ('Z1'/'Z2'/'Z3') rides alongside `lot_verification_level`
// in the same snapshot (Groovepacker::Inventory::LotSnapshot#to_h merges
// Groovepacker::Inventory::ScanLadder#to_h's `zero_inventory_level`/`zero_inventory_mode`, and
// OrderItem assigns that snapshot to `result['wms_inventory']` — see
// lib/groovepacker/inventory/scan_ladder.rb:113-119 and lot_snapshot.rb:66-68 on the backend).
// This mirrors `checkWmsLotVerification` immediately above, which already reads the sibling
// `lot_verification_level` key correctly — this function previously did not make the analogous
// check and gated EVERY zero/negative item regardless of configured rung, which meant every Z1
// tenant (the DEFAULT_ZERO_LEVEL) got a blocking round trip + full-screen loading overlay
// (see SuggestedItemSection.js's ActivityIndicator) on every scan of a zero/oversold item — the
// same items a packer scans repeatedly, and exactly what Z1/Z2 are specified to NOT interrupt.
//
// Fails toward NOT gating (return false) when `zero_inventory_level` is missing or unrecognised —
// a misconfigured/absent setting must never stop a packing station, matching ScanLadder's own
// documented fail-open posture.
export const shouldGateForZeroInventory = (item) => {
  const wmsInventory = item?.wms_inventory;
  if (!wmsInventory) return false;
  if (wmsInventory.zero_inventory_level !== 'Z3') return false;
  return Number(wmsInventory.quantity_on_hand) <= 0;
};

// Advisory-only fast-fail for the receive quantity, mirrored against the server's own validation
// ("Enter both a lot number and a quantity of at least 1."). A local pass here is NEVER treated
// as authorization — only the server's response (no further `receiving` ask) commits the scan.
// Must reject: 0, negatives, blank/whitespace, non-numeric, and decimals; accept only a positive
// integer (optionally with surrounding whitespace).
export const validateWmsZeroReceiveQty = (given) => {
  const trimmed = String(given ?? '').trim();
  return /^[1-9]\d*$/.test(trimmed);
};

